<template>
    <div>
        <div
            v-if="isEditingFrequencySet"
            class="module"
            data-test-id="frequency-set-view"
        >
            <header class="module__header">
                <a
                    class="back-btn"
                    @click="isEditingFrequencySet = false"
                >
                    <span class="icon-chevron-left" />
                </a>
                <h1 class="has-back-btn">Create {{ editingFrequencySetType.label }} Frequency Set</h1>
            </header>
            <section class="module__body">
                <create-frequency-set
                    v-if="editingFrequencySetType.type === frequencySetTypes.X_SERIES.type"
                    :create-endpoint="createFrequencySetEndpoint"
                    :show-save-and-cancel="true"
                    :frequency-set="createdFrequencySet"
                    @cancel-button-clicked="isEditingFrequencySet = false"
                    @frequency-set-created="frequencySetChanged"
                />
                <create-nextgen-frequency-set
                    v-else
                    :create-endpoint="createFrequencySetEndpoint"
                    :show-save-and-cancel="true"
                    :frequency-set="createdFrequencySet"
                    @cancel-button-clicked="isEditingFrequencySet = false"
                    @frequency-set-created="frequencySetChanged"
                />
            </section>
        </div>
        <div
            v-else-if="isEditingEncryptionKey"
            class="module"
            data-test-id="encryption-key-view"
        >
            <header class="module__header">
                <a
                    class="back-btn"
                    @click="isEditingEncryptionKey = false"
                >
                    <span class="icon-chevron-left" />
                </a>
                <h1 class="has-back-btn">Create Encryption Key</h1>
            </header>
            <section class="module__body">
                <create-encryption-key
                    :create-endpoint="`/${organizationSlug}/configuration-sets/validate-encryption-key`"
                    :show-save-and-cancel="true"
                    :encryption-key="createdEncryptionKey"
                    @cancel-button-clicked="isEditingEncryptionKey = false"
                    @encryption-key-created="encryptionKeyChanged"
                />
            </section>
        </div>
        <div
            v-else
            class="module"
            data-test-id="configuration-set-view"
        >
            <header class="module__header">
                <a
                    class="back-btn"
                    @click="goBack('admin-configuration-sets')"
                >
                    <span class="icon-chevron-left" />
                </a>
                <h1 class="has-back-btn">{{ isEditing ? 'Edit' : 'Create' }} Configuration Set</h1>
            </header>
            <section class="module__body">
                <form
                    v-if="formLoaded"
                    @submit.prevent="saveConfiguration"
                >
                    <div class="form-group">
                        <label for="name">Configuration Set Name</label>
                        <input
                            id="name"
                            v-model="name"
                            type="text"
                            data-test-id="config-set-name"
                            required
                            minlength="3"
                            maxlength="20"
                        />
                    </div>

                    <div class="form-group">
                        <label>PLI Interval (optional)</label>
                        <slider-toggle-field
                            label="Position Location Updates"
                            :value="pliEnabled"
                            data-test-id="pli-enabled-toggle"
                            @update="togglePli"
                        ></slider-toggle-field>
                    </div>
                    <div
                        v-if="pliEnabled"
                        class="form-group"
                    >
                        <tab-view
                            :activeTab="pliIntervalTabIndex"
                            :tabs="pliIntervalTypeTabs"
                            data-test-id="config-set-pli-interval-type"
                            @tab-clicked="setPliIntervalTab"
                        ></tab-view>
                    </div>
                    <div
                        v-if="pliEnabled"
                        class="form-group"
                    >
                        <select
                            id="pliInterval"
                            v-model="pli_interval_value"
                            data-test-id="config-set-pli-interval"
                            required
                        >
                            <option
                                v-for="(label, value) in pliIntervalOptions"
                                :key="`b${value}`"
                                :data-test-id="`interval-choice-${value}`"
                                :value="value"
                            >
                                {{ label }}
                            </option>
                        </select>
                    </div>
                    <div
                        v-if="
                            pliEnabled && pliIntervalTabs[pliIntervalTabIndex] === configurationSetPliIntervalTypes.TIME
                        "
                        class="form-group"
                    >
                        <select
                            id="pliJittering"
                            v-model="pli_interval_time_jitter"
                            data-test-id="config-set-time-jitter"
                            required
                        >
                            <option
                                v-for="(label, value) in pliJitterValues"
                                :key="`b${value}`"
                                :data-test-id="`jitter-choice-${value}`"
                                :value="value"
                            >
                                Jittering: {{ label }}
                            </option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label>Frequency Set</label>
                        <frequency-set-select
                            v-if="createdFrequencySet === null"
                            v-model="frequency_set_id"
                            :frequency-sets="frequencySets"
                            placeholder="Select a Frequency Set"
                            test-id="config-set-select-frequency-set"
                            @change="selectedFrequencySetChanged"
                        >
                            <template #footer>
                                <div
                                    v-for="frequencySetType in creatableFrequencySetTypes"
                                    :key="frequencySetType.type"
                                    class="p-3"
                                >
                                    <Prime-Button
                                        fluid
                                        severity="secondary"
                                        text
                                        class="create-frequency-set-button"
                                        :aria-label="`Create ${frequencySetType.label} Frequency Set`"
                                        :data-test-id="frequencySetType.testId"
                                        @click.prevent="showCreateFrequencySet(frequencySetType)"
                                    >
                                        <span class="pi pi-plus" />
                                        <span>Create</span>
                                        <frequency-set-type-badge
                                            :type="frequencySetType.type"
                                            :data-test-id="`${frequencySetType.testId}-type`"
                                        />
                                    </Prime-Button>
                                </div>
                            </template>
                        </frequency-set-select>
                        <div
                            v-else
                            class="created-item"
                        >
                            <div class="disabled-field">
                                <span
                                    class="name"
                                    data-test-id="frequency-set-name"
                                    >{{ createdFrequencySet ? createdFrequencySet.name : '' }}</span
                                >
                                <frequency-set-type-badge
                                    :type="createdFrequencySet ? createdFrequencySet.type : null"
                                    data-test-id="created-frequency-set-type"
                                />
                            </div>
                            <a
                                class="edit"
                                data-test-id="edit-frequency-set-button"
                                @click.prevent="editCreatedFrequencySet"
                            />
                            <a
                                class="delete"
                                data-test-id="delete-frequency-set-button"
                                @click.prevent="createdFrequencySetDeleted"
                            />
                        </div>
                    </div>
                    <div
                        v-if="previewFrequencySet"
                        class="form-group"
                        data-test-id="frequency-set-preview"
                    >
                        <frequency-set-preview :frequency-set="previewFrequencySet" />
                    </div>

                    <div class="form-group">
                        <label>Default Encryption Key (optional)</label>
                        <Prime-Select
                            v-if="createdEncryptionKey === null"
                            v-model="encryption_key_id"
                            :options="encryptionKeys"
                            optionLabel="name"
                            optionValue="id"
                            filter
                            placeholder="Select an Encryption Key"
                            data-test-id="config-set-select-encryption-key"
                            :pt="{
                                option: ({ context }) => ({
                                    'data-test-id': `encryption-key-option-${context.option.id}`,
                                }),
                                pcFilter: {
                                    root: {
                                        'data-test-id': 'encryption-key-list-search-box',
                                    },
                                },
                            }"
                        >
                            <template #footer>
                                <div class="p-3">
                                    <Prime-Button
                                        label="Create New Encryption Key"
                                        fluid
                                        severity="secondary"
                                        text
                                        icon="pi pi-plus"
                                        data-test-id="add-encryption-key-button"
                                        @click.prevent="showCreateEncryptionKey"
                                    />
                                </div>
                            </template>
                        </Prime-Select>
                        <div
                            v-else
                            class="created-item"
                        >
                            <input
                                disabled
                                :value="createdEncryptionKey ? createdEncryptionKey.name : ''"
                                data-test-id="encryption-key-name"
                            />
                            <a
                                class="edit"
                                data-test-id="edit-encryption-key-button"
                                @click.prevent="isEditingEncryptionKey = true"
                            />
                            <a
                                class="delete"
                                data-test-id="delete-encryption-key-button"
                                @click.prevent="createdEncryptionKey = null"
                            />
                        </div>
                    </div>

                    <form-errors :response="errorResponse" />

                    <div class="form-group">
                        <button
                            type="submit"
                            class="btn btn-primary"
                            data-test-id="config-set-submit"
                            :class="{ active: isSubmitted }"
                        >
                            <div>{{ isEditing ? 'Update' : 'Create' }}</div>
                        </button>
                    </div>
                </form>
                <ul class="warning-list">
                    <li
                        v-if="isEditing"
                        class="form-help"
                    >
                        This action only affects this Configuration Set. Encryption Keys and Frequency Sets used
                        elsewhere are not changed.
                    </li>
                    <li
                        v-if="frequencySetIsType(frequencySetTypes.NEXT_GEN)"
                        class="form-help important"
                        data-test-id="nextgen-frequency-set-warning"
                    >
                        This Frequency Set is only compatible with {{ frequencySetTypes.NEXT_GEN.label }} radios and
                        will not work with {{ frequencySetTypes.X_SERIES.label }} radios.
                    </li>
                </ul>
            </section>
        </div>
    </div>
</template>

<script>
import PrimeSelect from 'primevue/select'
import PrimeButton from 'primevue/button'
import _ from 'lodash'
import FormErrors from '@/components/FormErrors.vue'
import SliderToggleField from '@/components/SliderToggleField.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import TabView from '@/components/TabView.vue'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import configurationSetPliIntervalTypes from '@/constants/configuration-set-pli-interval-types.js'
import configurationSetValues from '@/mixins/configurationSetValues.js'
import CreateEncryptionKey from '@/components/CreateEncryptionKey.vue'
import CreateFrequencySet from '@/components/CreateFrequencySet.vue'
import CreateNextGenFrequencySet from '@/components/CreateNextGenFrequencySet.vue'
import FrequencySetPreview from '@/components/ConfigurationSets/FrequencySetPreview.vue'
import FrequencySetSelect from '@/components/FrequencySetSelect.vue'
import FrequencySetTypeBadge from '@/components/FrequencySetTypeBadge.vue'
import frequencySetTypes, { byValue as frequencySetTypeByValue } from '@/constants/frequency-set-types.js'
import featureFlags from '@/constants/feature-flags.js'

export default {
    components: {
        FormErrors,
        TabView,
        PrimeSelect,
        PrimeButton,
        FrequencySetSelect,
        FrequencySetTypeBadge,
        SliderToggleField,
        CreateEncryptionKey,
        CreateFrequencySet,
        'create-nextgen-frequency-set': CreateNextGenFrequencySet,
        FrequencySetPreview,
    },

    mixins: [canToast, hasBackButton, needsOrganizationSlug, configurationSetValues],

    data() {
        return {
            id: this.$route.params.id ?? null,
            name: null,
            pli_interval_type: null,
            pli_interval_value: null,
            pli_interval_time_jitter: null,
            frequency_set_id: null,
            encryption_key_id: null,
            frequencySets: [],
            encryptionKeys: [],
            configurationSetPliIntervalTypes,
            formLoaded: false,
            isSubmitted: false,
            errorResponse: null,
            isEditingFrequencySet: false,
            isEditingEncryptionKey: false,
            previewFrequencySet: null,
            createdFrequencySet: null,
            initialEncryptionKey: null,
            createdEncryptionKey: null,
            frequencySetTypes,
            editingFrequencySetType: null,
        }
    },

    computed: {
        isEditing() {
            return this.id !== null
        },
        pliIntervalTypeTabs() {
            return Object.keys(configurationSetPliIntervalTypes).map((intervalType) => {
                return { label: _.capitalize(intervalType) }
            })
        },
        pliIntervalOptions() {
            if (this.pliIntervalTabs[this.pliIntervalTabIndex] === configurationSetPliIntervalTypes.TIME) {
                return this.pliIntervalTimes
            }
            return this.pliIntervalDistances
        },
        createFrequencySetEndpoint() {
            return (
                `/${this.organizationSlug}/configuration-sets/validate-frequency-set` +
                `?type=${this.editingFrequencySetType.type}`
            )
        },
        hasNextGenFrequencySetsFeatureFlag() {
            return this.$store.getters.hasFeatureFlag(featureFlags.NEXTGEN_FREQUENCY_SETS_ENABLED)
        },

        // The types a new frequency set can be created as, each with the test id of its button.
        creatableFrequencySetTypes() {
            const types = [{ ...frequencySetTypes.X_SERIES, testId: 'add-x-series-frequency-set-button' }]

            if (this.hasNextGenFrequencySetsFeatureFlag) {
                types.push({ ...frequencySetTypes.NEXT_GEN, testId: 'add-next-gen-frequency-set-button' })
            }

            return types
        },
    },

    mounted() {
        const promises = [this.fetchEncryptionKeys(), this.fetchFrequencySets()]

        if (this.isEditing) {
            promises.push(this.fetchConfigurationSet())
        }

        Promise.all(promises).then(() => {
            if (this.isEditing) {
                // add selected frequency set and encryption key if not in list of all items
                if (
                    this.frequency_set_id !== null &&
                    !this.frequencySets.find((freqSet) => freqSet.id === this.frequency_set_id)
                ) {
                    this.frequencySets.push(this.previewFrequencySet)
                }

                if (
                    this.encryption_key_id !== null &&
                    !this.encryptionKeys.find((key) => key.id === this.encryption_key_id)
                ) {
                    this.encryptionKeys.push(this.initialEncryptionKey)
                }
            }
            this.formLoaded = true
        })
    },

    methods: {
        saveConfiguration() {
            let xhrMethod = this.$http.post
            let verb = 'created'
            let url = `/${this.organizationSlug}/configuration-sets`

            if (this.isEditing) {
                xhrMethod = this.$http.put
                verb = 'updated'
                url += `/${this.id}`
            }

            this.isSubmitted = true
            xhrMethod(url, {
                name: this.name,
                pli_interval_type: this.pli_interval_type,
                pli_interval_value: this.pli_interval_value,
                pli_interval_time_jitter: this.pli_interval_time_jitter,
                frequency_set_id: this.frequency_set_id,
                created_frequency_set: this.createdFrequencySet,
                encryption_key_id: this.encryption_key_id,
                created_encryption_key: this.createdEncryptionKey,
            })
                .then(() => {
                    this.toast(`Configuration Set successfully ${verb}`)
                    this.$router.push(`/${this.organizationSlug}/configuration-sets`)
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.isSubmitted = false
                })
        },

        createdFrequencySetDeleted() {
            this.createdFrequencySet = null
            this.previewFrequencySet = null
        },

        selectedFrequencySetChanged() {
            if (!this.frequency_set_id) {
                this.previewFrequencySet = null
            } else {
                this.$http.get(`/${this.organizationSlug}/frequency-sets/${this.frequency_set_id}`).then(({ data }) => {
                    this.previewFrequencySet = data
                })
            }
        },

        fetchFrequencySets() {
            return this.$http.get(`/${this.organizationSlug}/frequency-sets/names`).then(({ data }) => {
                this.frequencySets = data
            })
        },

        fetchEncryptionKeys() {
            return this.$http.get(`/${this.organizationSlug}/encryption-keys/names`).then(({ data }) => {
                this.encryptionKeys = data
            })
        },

        fetchConfigurationSet() {
            return this.$http.get(`/${this.organizationSlug}/configuration-sets/${this.id}`).then(({ data }) => {
                this.name = data.name
                this.pli_interval_type = data.pli_interval_type
                this.pli_interval_value = data.pli_interval_value
                this.pli_interval_time_jitter = data.pli_interval_time_jitter
                this.encryption_key_id = data.encryption_key_id
                this.initialEncryptionKey = data.encryption_key
                this.frequency_set_id = data.frequency_set_id
                this.previewFrequencySet = data.frequency_set

                this.pliEnabled = this.pli_interval_type !== null
                this.setTab(this.pli_interval_type)
            })
        },

        setPliIntervalTab(index) {
            if (index !== this.pliIntervalTabIndex) {
                this.pliIntervalTabIndex = index
                this.resetPliFields()
            }
        },

        showCreateEncryptionKey() {
            this.isEditingEncryptionKey = true
        },

        showCreateFrequencySet(type) {
            this.isEditingFrequencySet = true
            this.editingFrequencySetType = type
        },

        editCreatedFrequencySet() {
            // Re-open the form for the type of the set being edited; fall back to
            // X Series for legacy sets created before the type column existed.
            this.editingFrequencySetType = this.createdFrequencySet?.type
                ? frequencySetTypeByValue(this.createdFrequencySet.type)
                : frequencySetTypes.X_SERIES
            this.isEditingFrequencySet = true
        },

        togglePli() {
            this.pliEnabled = !this.pliEnabled
            this.resetPliFields()
        },

        resetPliFields() {
            if (!this.pliEnabled) {
                this.pli_interval_value = null
                this.pli_interval_time_jitter = null
                this.pli_interval_type = null
                this.pliIntervalTabIndex = 0
            } else if (this.pliIntervalTabs[this.pliIntervalTabIndex] === configurationSetPliIntervalTypes.TIME) {
                this.pli_interval_value = this.defaultPliIntervalTime
                this.pli_interval_time_jitter = this.defaultPliJitterValue
                this.pli_interval_type = configurationSetPliIntervalTypes.TIME
            } else {
                this.pli_interval_type = configurationSetPliIntervalTypes.DISTANCE
                this.pli_interval_value = this.defaultPliIntervalDistance
                this.pli_interval_time_jitter = null
            }
        },

        frequencySetChanged(frequencySet) {
            this.createdFrequencySet = frequencySet
            this.previewFrequencySet = frequencySet
            this.isEditingFrequencySet = false
            this.frequency_set_id = null
        },

        encryptionKeyChanged(encryptionKey) {
            this.createdEncryptionKey = encryptionKey
            this.isEditingEncryptionKey = false
            this.encryption_key_id = null
        },

        frequencySetIsType(type) {
            return this.previewFrequencySet?.type === type.type
        },
    },
}
</script>
<style scoped lang="scss">
// PrimeVue shrinks badges inside a button (.p-button .p-badge) down to its own badge token,
// which flattens ours; restore the badge's normal size.
.create-frequency-set-button .p-badge {
    min-width: var(--p-badge-min-width, 1.5rem);
    height: var(--p-badge-height, 1.5rem);
    line-height: normal;
}

.module__body {
    display: flex;

    form {
        max-width: 360px;
    }

    .form-group.action-buttons {
        text-align: right;

        button {
            margin-left: 20px;
        }
    }

    .p-select {
        width: 100%;
        height: 36px;
    }

    .created-item {
        display: flex;
        align-items: center;
        justify-content: center;

        .disabled-field {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
            min-width: 0;
            height: 36px;
            padding: 0 12px;
            border-radius: 5px;
            background-color: #ededed;
            color: #444;
            font-family: 'Avenir Book';
            font-size: 0.9375rem;
            cursor: not-allowed;

            .name {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                opacity: 0.6;
            }

            .p-badge {
                flex-shrink: 0;
            }
        }

        a {
            background-size: 20px 20px;
            background-repeat: no-repeat;
            background-position: top left;
            width: 20px;
            height: 20px;
            display: block;
            margin-left: 15px;
            cursor: pointer;

            &.edit {
                background-image: url(@images/icon-edit.svg);
            }

            &.delete {
                background-image: url(@images/delete-white.svg);
            }
        }
    }

    .warning-list {
        li {
            margin-bottom: 10px;
        }
    }
}
</style>
